import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// The corridor IS the image, edge to edge, and it is emissive. A rig would be
// a light source inside a space that has no consistent up.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'vertigo',
  family: 'shader',
  danceability: 0.88,
  energyLevel: 0.86,
  bpmMin: 120,
  bpmMax: 185,
  movement: 0.85,
  tonality: 0.45,
  prefers: { gridStrength: [0.65, 1.0], percussiveness: [0.40, 1.0] },
  enabled: true
};

// Vertigo: an impossible architecture whose geometry turns itself inside out on
// the beat. It does not display a shape - it destabilises the reading of space.
//
// The base is an infinite corridor in first person: floor, ceiling and two
// walls, all chequered, all running to a vanishing point. Everything else is
// DEFORMATION of that space, and the whole piece turns on one rule: never one
// deformation at a time. One reads as an effect applied to a corridor. Two
// superimposed read as a corridor that cannot exist, because the eye keeps
// trying to resolve them into a single consistent space and cannot.
// ?vertsingle=1 is the demonstration.
//
// TWO THINGS ARE NOT WHAT THEY LOOK LIKE HERE, and both are load-bearing:
//
// 1. THE CAMERA NEVER MOVES. An infinite corridor is invariant along its own
//    axis, so advancing one tile per beat is the same thing as scrolling the
//    chequer coordinate by one tile per beat - and scrolling has no far end to
//    pop in, no geometry to recycle, and no accumulating float error after
//    twenty minutes. The geometry is built once and stays put.
// 2. THE CHEQUER'S FILTER WIDTH IS COMPUTED BY HAND, per vertex, from a finite
//    difference of the fully deformed projection. See vFoot below - this is
//    the one part of the file that would be three lines with fwidth() and is
//    not, for a reason this codebase has already settled twice.

// --- the corridor ------------------------------------------------------------
// Cross-section half-size. The corridor is square in section and the camera
// sits on its axis, so this is also the distance to every wall.
const R = 1.0;
// How far the geometry runs. 80 is well past the point where a tile is smaller
// than a pixel and the chequer has already gone flat grey, so nothing is
// gained by more.
const FAR = 160.0;
// Vertices are distributed uniformly in 1/depth, which is uniform in SCREEN
// space - the only distribution that does not waste most of them on the far
// third of the corridor, which occupies a few dozen pixels.
//
// The near end is computed from the aspect rather than fixed: the first thing
// to enter frame is the CORNER of the square cross-section aligned with the
// corner of the frame, and at 3.2:1 that happens twice as early as at 16:9. A
// fixed near end leaves a wedge of background showing at the edges.
const NEAR_MARGIN = 0.70;      // and start this much earlier again, for the warps
// THE CAMERA DOES NOT STAY ON THE AXIS, whatever the note at the top of this
// file says. scene-manager drifts it by DRIFT_POSITION_AMOUNT around whatever
// pose a visualizer last wrote, and this one writes its pose once in init()
// and never again - so the drift simply accumulates against it. Measured
// live: |x| reaches 0.218.
//
// That is the whole bug. The near section has to contain the frustum, and at
// 3.2:1 it did so with 0.055 units to spare - four times less than the drift.
// The camera was being carried out through its own corridor wall, which shows
// as a black band down one side, intermittently, and worse under the warps
// because they shrink the section further. Hence "hard to reproduce": it
// needs the drift near its extreme, pointing the right way.
//
// The fix is the one that was asked for - give the camera more room by
// starting the tube nearer - but it is computed rather than dialled, because
// the margin needed depends on the aspect and runs out first on the widest
// frames.
const CAMERA_DRIFT = 0.24;     // scene-manager's 0.22, plus a little

// --- the beat ----------------------------------------------------------------
// EVERYTHING IS A FUNCTION OF ONE BEAT COUNTER. When there is a real grid the
// counter is read from it; when there is not - no bpm, which is a radio stream
// and the first seconds of every track - it free-runs at the nominal tempo.
// Cascade had to be repaired for exactly this, so it is built in from the
// start rather than bolted on: the piece cannot tell which one it is getting.
const SYNTH_BPM = 138;         // techno/psytrance territory, per the profile
const BEATS_PER_BAR = 4;
const BEATS_PER_PHRASE = 16;

// --- the deformations --------------------------------------------------------
const W_CONVEX = 0;
const W_CONCAVE = 1;
const W_TWIST = 2;
const W_FLIP = 3;
const W_FOLD = 4;
const W_STRAIGHT = 5;
const WARP_NAMES = ['convex', 'concave', 'twist', 'flip', 'fold', 'straight'];
// CONVEX against CONCAVE is the one forbidden pair: they are the same warp with
// opposite signs, so together they cancel to a straight corridor with extra
// arithmetic. Every other pair produces something.
const FORBIDDEN = [[W_CONVEX, W_CONCAVE]];

const AMP_A = [0.55, 1.00];    // the dominant warp
const AMP_B = [0.22, 0.58];    // and the one that makes it impossible
const AMP_LOW_LO = 0.70;       // bands.low scales both
const AMP_LOW_HI = 1.25;
// And a ceiling on the product. The warps are written for an amplitude of
// about one; the shader clamps the two that would break outright, this keeps
// the rest from looking merely wrong.
const AMP_CEIL = 1.15;

// COMFORT LIMIT, and the reason TWIST is not stronger. The brief's ceiling is
// 90 degrees per second of vanishing-point rotation; TWIST turns the far end by
// TWIST_MAX radians and the warp changes at most once per beat, so at the
// profile's top tempo of 185 bpm a full swing from -TWIST_MAX to +TWIST_MAX
// takes one beat, 0.324 s. 2 * TWIST_MAX / 0.324 <= pi/2 rad/s gives
// TWIST_MAX <= 0.254. Rounded down.
const TWIST_MAX = 0.25;

// --- the vanishing point -----------------------------------------------------
const VP_DRIFT = 0.055;        // cross-section units per second, continuous
const VP_JUMP = 0.42;          // and this much, instantly, on a bar
const VP_MAX = 0.75;           // never further off-axis than this

// --- the chequer -------------------------------------------------------------
const TILES_MIN = 6;           // tiles across the corridor at the track's ends
const TILES_MAX = 14;          // and in the middle
// COMFORT LIMIT: the chequer must not invert more than twice a second. Colours
// swap on bar downbeats, which at 185 bpm is every 1.30 s, so the musical rule
// already satisfies it - this floor only guards the drop, which can otherwise
// land an inversion on top of a bar's.
const INVERT_MIN_S = 0.5;
const SAT_MIN = 0.85;
// Per-face variants of the same pair: enough to tell the four surfaces apart,
// not enough to break them into four different materials. Floor is the
// reference; the ceiling reads darker because a ceiling does.
const FACE_TINT = [1.00, 0.72, 0.88, 0.80];

// --- the light tunnel --------------------------------------------------------
const RING_SIGMA = 0.055;      // in tile units, so a ring is a fraction of a tile
const RING_GAIN_LO = 0.30;
const RING_GAIN_HI = 0.95;     // bands.high
const RING_FALLOFF = 0.055;    // per unit of depth

// --- levels ------------------------------------------------------------------
// Flat saturated colour over the whole frame is the one thing that turns to
// mush under bloom (threshold 0.78 - see scene-manager), so the chequer lives
// well under it and only the rings are allowed near the top.
const PEAK = 0.80;
const BASE_HI = 0.40;          // the lit square's luminance
const BASE_LO = 0.13;          // and the dark one's

// --- drops -------------------------------------------------------------------
const DROP_RAMP_BEATS = 4;
const DROP_RAMP_AMP = 1.65;
const INVERT_BEATS = 1;

const TIER = {
  // Tier 0 keeps ALL FOUR FACES. The brief economises by dropping to floor and
  // ceiling, and measured that does not degrade the image, it deletes it: the
  // two missing walls are two black bands down the sides of the frame and what
  // is left is not a corridor. The saving comes out of the subdivision instead,
  // which is where the vertex cost actually is - 4 x 48 x 48 x 3 transforms is
  // fewer than the 2 x 64 x 64 x 3 the brief's split would have cost, so this
  // is cheaper AND complete.
  0: { seg: 48, faces: 4, rings: false },
  1: { seg: 96, faces: 4, rings: true },
  2: { seg: 128, faces: 4, rings: true }
};
const DEFAULT_TIER = 2;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// ============================================================================
// Vertex shader.
//
// HIGHP, not mediump, and this is the one place the brief's "precision
// mediump" is deliberately not followed. Two reasons, both fatal on the TV,
// where mediump is genuinely fp16:
//   - depths run to 80 and fp16 spacing at 80 is 0.06, so the far half of the
//     corridor would z-fight against itself;
//   - vFoot is a FINITE DIFFERENCE of two projected screen positions, i.e. a
//     subtraction of two nearly equal numbers around 1000 px, where fp16
//     spacing is 1.0. The difference would quantise to whole pixels and the
//     chequer's filter width - the thing standing between this image and an
//     unwatchable shimmer - would be noise.
// The fragment shader, which only ever handles small numbers, is mediump as
// asked.
// ============================================================================
const vertexShader = `
  precision highp float;

  attribute float aFace;

  uniform float uInvNear, uInvFar;
  uniform float uTile;          // corridor units per tile
  uniform float uTravel;        // tiles advanced, one per beat
  uniform vec2 uVP;             // vanishing-point offset, cross-section units
  uniform float uWarpA, uWarpB;
  uniform float uAmpA, uAmpB;
  uniform float uInvert;        // the drop's total inversion, 0..1
  uniform float uGuard;         // s at which the deformations reach full strength
  uniform vec2 uHalfRes;        // render buffer, in pixels / 2
  uniform vec2 uStep;           // finite-difference step in (s, t)

  varying vec2 vChecker;        // tile coordinates, along and across
  varying vec2 vFoot;           // their footprint in ONE pixel
  varying float vFace;
  varying float vDepth;
  varying vec4 vClip;

  float depthAt(float s) {
    return 1.0 / mix(uInvNear, uInvFar, s);
  }

  // The undeformed cross-section, in units of the corridor's half-size.
  // 0 floor, 1 ceiling, 2 left wall, 3 right wall.
  vec2 baseCross(float t, float face) {
    float a = t * 2.0 - 1.0;
    if (face < 0.5) return vec2(a, -1.0);
    if (face < 1.5) return vec2(a, 1.0);
    if (face < 2.5) return vec2(-1.0, a);
    return vec2(1.0, a);
  }

  // One deformation, applied in place. zn is the position along the corridor
  // in SCREEN terms (s is uniform in 1/depth, which is uniform on screen), so
  // "stronger with distance" means stronger toward the middle of the frame,
  // which is where it is read.
  void applyWarp(float id, float amp, inout vec2 cs, inout float depth, float zn) {
    if (id < 0.5) {
      // CONVEX - the floor domes up toward the viewer, the ceiling presses
      // down. The section closes into a tube.
      float a0 = min(amp, 1.2);
      cs.y += a0 * (1.0 - cs.x * cs.x) * (0.35 + 0.95 * zn);
      cs.y *= 1.0 - 0.30 * a0;
    } else if (id < 1.5) {
      // CONCAVE - the same thing inverted: the floor drops into a funnel.
      float a1 = min(amp, 1.2);
      cs.y -= a1 * (1.0 - cs.x * cs.x) * (0.35 + 0.95 * zn);
      cs.y *= 1.0 + 0.30 * a1;
    } else if (id < 2.5) {
      // TWIST - the corridor screws about its own axis, harder with distance,
      // so the vanishing point turns.
      float a = amp * ${TWIST_MAX.toFixed(3)} * 6.2831853 * zn;
      float c = cos(a), s2 = sin(a);
      cs = mat2(c, -s2, s2, c) * cs;
    } else if (id < 3.5) {
      // FLIP - a uniform roll of the whole corridor. At amp = 1 the floor is
      // overhead wearing the floor's own colour variant, which is what makes
      // it unreadable rather than merely upside down. Uniform, unlike TWIST,
      // so the two never look like each other.
      float a = amp * 3.1415927;
      float c = cos(a), s2 = sin(a);
      cs = mat2(c, -s2, s2, c) * cs;
    } else if (id < 4.5) {
      // FOLD - past the halfway point the corridor curls up and back over
      // itself, so its far half is seen from above and its perspective runs
      // the wrong way.
      //
      // ON THE SECTION, NEVER ON DEPTH, and that is the whole lesson of this
      // warp. Hinging depth back through the halfway point is the literal
      // reading and it does two things wrong at once: it sends the far end
      // BEHIND THE LENS (measured, depth -85 at the far station, so the far
      // half of the tube simply stops existing), and it destroys the one
      // property that guarantees the frame is covered at all - that depth is
      // monotone along s, which is what makes the corridor a closed surface a
      // ray cannot escape without hitting it. Measured with CONCAVE, 39% of
      // the frame went black. Curling the section instead keeps both: the far
      // half rises overhead and flares open, which is what "seen from above"
      // looks like from inside, and every ray still lands on something.
      float e = max(0.0, zn - 0.5) * 2.0;
      float m = min(amp, 1.0);
      cs.y += m * 3.0 * e * e;
      cs *= 1.0 + m * 1.6 * e * e;
    }
    // id 5 is STRAIGHT: the breath, and what makes the other five legible.
  }

  // The full transform, clip space out. Called three times per vertex - once
  // for the vertex and twice at a small offset - which is how vFoot is
  // obtained without GL_OES_standard_derivatives.
  vec4 place(float s, float t, float face) {
    float depth = depthAt(s);
    vec2 cs = baseCross(t, face);

    // THE NEAR END IS NEVER DEFORMED, and this is what keeps the frame filled.
    //
    // The corridor is a closed tube, so a ray that starts INSIDE its section
    // can only ever leave through a wall - which means it hits one. The single
    // condition for the frame to be covered is therefore that the section at
    // the nearest end contains the whole frustum, and that is arranged by
    // construction (see NEAR_MARGIN). The deformations broke it: CONVEX shifts
    // the floor up by 0.42 and then scales the section by 0.64, so the near
    // section collapsed from a half-size of 1.0 to 0.37 while the frustum
    // corner needs 0.99 - the rays started OUTSIDE the tube and never met it.
    // Measured, CONVEX with FLIP blacked out a third of the frame's edge band,
    // and plain CONVEX 19%. FLIP alone is the same trap by another route: a
    // square turned 45 degrees presents its edge at radius 1.0, not its corner
    // at 1.414.
    //
    // So every deformation ramps in from zero over the stretch of corridor
    // that is BEHIND the frame - out to where the undeformed tube first
    // touches the frame corner, which is uGuard, computed per aspect. Nothing
    // is lost: that stretch cannot be seen. What can be seen is deformed in
    // full, starting at the first pixel where it exists.
    float g = smoothstep(0.0, uGuard, s);

    // THE VANISHING POINT, as a BEND rather than a tilt. Offsetting the
    // section proportionally to s - which saturates at 1 - curves the corridor
    // and leaves the camera inside it at every depth. Offsetting proportionally
    // to DEPTH, which is what an actually tilted corridor does, puts the
    // section centre further from the axis than its own half-width past a
    // depth of 1/|uVP|: the camera ends up outside its own corridor and the
    // far half is seen from the wrong side. One multiply-add, and it is the
    // single most destabilising thing in the file.
    cs += uVP * s * g;
    applyWarp(uWarpA, uAmpA * g, cs, depth, s);
    applyWarp(uWarpB, uAmpB * g, cs, depth, s);
    if (uInvert > 0.001) {
      // Turned inside out: a circle inversion of the section, so what was on
      // the axis is flung to the walls and what was at the walls collapses
      // inward. Clamped, or the axis goes to infinity.
      float q = dot(cs, cs);
      float k = clamp(1.0 / max(q, 0.04), 0.30, 3.2);
      cs = mix(cs, cs * k, uInvert * g);
    }
    vec3 P = vec3(cs * ${R.toFixed(2)}, -depth);
    return projectionMatrix * modelViewMatrix * vec4(P, 1.0);
  }

  vec2 toPixels(vec4 clip) {
    // Behind the lens there is no meaningful screen position. Returning a
    // point far away makes the footprint enormous, which sends the chequer to
    // flat grey - the correct answer for geometry that is folding through the
    // camera.
    float w = max(clip.w, 0.02);
    return clip.xy / w * uHalfRes;
  }

  void main() {
    float s = uv.x, t = uv.y;
    vFace = aFace;

    // THE STEP TURNS BACKWARD AT THE EDGES, and this is not a detail. A
    // forward step from s = 1 extrapolates the 1/depth ramp past uInvFar and
    // straight through zero into NEGATIVE depth - at 128 segments the last row
    // lands at depth -103 - so the footprint would be nonsense exactly at the
    // vanishing point, which is the one place in the frame that cannot survive
    // a wrong filter width. The difference is one-sided either way and only
    // its magnitude is used.
    float s1 = (s + uStep.x > 1.0) ? s - uStep.x : s + uStep.x;
    float t1 = (t + uStep.y > 1.0) ? t - uStep.y : t + uStep.y;

    vec4 c0 = place(s, t, aFace);
    vec4 c1 = place(s1, t, aFace);
    vec4 c2 = place(s, t1, aFace);
    vec2 p0 = toPixels(c0), p1 = toPixels(c1), p2 = toPixels(c2);

    float depth = depthAt(s);
    vDepth = depth;

    // THE CHEQUER RIDES ON THE SURFACE, so its coordinates are a function of
    // the UNDEFORMED parametrisation and their derivatives are exact rather
    // than differenced. Only the screen footprint of a step in (s, t) needs
    // the deformation, and that is what p1/p2 measure.
    float along = depth / uTile + uTravel;
    float across = (t * 2.0 - 1.0) * ${R.toFixed(2)} / uTile;
    vChecker = vec2(along, across);

    float dAlong = abs(depthAt(s1) - depth) / uTile;
    float dAcross = abs(t1 - t) * 2.0 * ${R.toFixed(2)} / uTile;
    float pxAlong = max(length(p1 - p0), 1e-4);
    float pxAcross = max(length(p2 - p0), 1e-4);
    vFoot = vec2(dAlong / pxAlong, dAcross / pxAcross);

    vClip = c0;
    gl_Position = c0;
  }
`;

// ============================================================================
// Fragment shader - mediump, as asked. Nothing here is larger than a few tens.
// ============================================================================
const fragmentShader = `
  precision mediump float;

  uniform vec3 uColA, uColB, uColC;
  uniform float uTri;          // 0 = two tones, 1 = the phrase's third colour
  uniform float uRingGain;
  uniform float uRings;
  uniform vec3 uRingCol;

  uniform float uAspect;

  varying vec2 vChecker;
  varying vec2 vFoot;
  varying float vFace;
  varying float vDepth;
  varying vec4 vClip;

  // Box-filtered chequerboard. The analytic average of the pattern over a
  // footprint of w tiles, rather than a point sample: it returns 0 or 1 where
  // the tiles are large, and goes to exactly 0.5 as they shrink below a pixel.
  // That limit IS the requirement - it is what a mipmap would do, and without
  // it the middle of the frame is a boiling mess of sub-pixel squares.
  float checkerBox(vec2 c, vec2 w) {
    vec2 a = (abs(fract((c - 0.5 * w) * 0.5) - 0.5)
            - abs(fract((c + 0.5 * w) * 0.5) - 0.5)) * 2.0 / max(w, vec2(1e-4));
    return 0.5 - 0.5 * a.x * a.y;
  }

  void main() {
    vec2 w = max(vFoot, vec2(1e-4));

    // The two-tone field, filtered.
    float k = checkerBox(vChecker, w);

    // THE THIRD COLOUR, for one bar per phrase. A box filter over three phases
    // has no closed form as cheap as the two-tone one, so this fades to the
    // pair's own mean exactly where the filter would have gone flat anyway -
    // the tiles are already below a pixel there and nothing is lost.
    float res = 1.0 - smoothstep(0.22, 0.70, max(w.x, w.y));
    float third = step(1.5, mod(floor(vChecker.x) + 2.0 * floor(vChecker.y), 3.0));

    vec3 pair = mix(uColB, uColA, k);
    vec3 col = mix(pair, mix(pair, uColC, third * res), uTri);

    // Per-face variants of the same pair, so the four surfaces separate.
    float tint = vFace < 0.5 ? ${FACE_TINT[0].toFixed(2)}
               : vFace < 1.5 ? ${FACE_TINT[1].toFixed(2)}
               : vFace < 2.5 ? ${FACE_TINT[2].toFixed(2)}
               : ${FACE_TINT[3].toFixed(2)};
    col *= tint;

    // THE LIGHT TUNNEL. Rings sit on the tile grid, so they arrive one per
    // beat for free, and because they are a function of the same chequer
    // coordinate they are deformed by whatever the corridor is doing - a ring
    // in a twisted corridor is itself twisted. The only additive element.
    if (uRings > 0.5) {
      float f = fract(vChecker.x);
      float e = min(f, 1.0 - f);
      float ring = exp(-e * e / (2.0 * ${RING_SIGMA.toFixed(3)} * ${RING_SIGMA.toFixed(3)}));
      // Blurred out by the same footprint that flattens the chequer, or the
      // rings alias exactly where the chequer no longer does.
      ring *= 1.0 - smoothstep(0.10, 0.45, w.x);
      ring *= exp(-vDepth * ${RING_FALLOFF.toFixed(3)});
      col += uRingCol * ring * uRingGain;
    }

    // NO SAFE ZONE HERE. It used to do two things - flatten the chequer to its
    // mean colour inside the cover's box, and darken it - and together they
    // put a plainly visible rectangle across the middle of the frame.
    // Measured: inside the box the pattern's standard deviation was 0.014
    // against 0.103 outside it, and the mean 0.108 against 0.208. With both
    // terms gone they are 0.047 and 0.146 - and what softness is left at the
    // centre is the perspective, not an attenuation: this is a tunnel, and its
    // tiles converge to a vanishing point there whatever anyone draws on top.
    //
    // That is a DEPARTURE from the shared contract every other visualizer
    // keeps, taken deliberately: the cover is opaque and sits on its own
    // shadow, and the rectangle was costing more than it bought.

    gl_FragColor = vec4(min(col, vec3(${PEAK.toFixed(2)})), 1.0);
  }
`;

const bgVertexShader = `
  precision mediump float;
  void main() { gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const bgFragmentShader = `
  precision mediump float;
  float bayer4(vec2 fc) {
    vec2 p1 = mod(fc, 2.0);
    vec2 p2 = floor(0.5 * mod(fc, 4.0));
    return 4.0 * mix(mix(0.0, 2.0, p1.y), mix(3.0, 1.0, p1.y), p1.x)
         +       mix(mix(0.0, 2.0, p2.y), mix(3.0, 1.0, p2.y), p2.x);
  }
  void main() {
    float d = (bayer4(floor(gl_FragCoord.xy)) / 16.0 - 0.5) / 255.0;
    gl_FragColor = vec4(vec3(0.010) + d, 1.0);
  }
`;

export class VertigoVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    const forced = urlParam('vertwarp');
    this._forcedWarp = WARP_NAMES.indexOf(forced);
    if (this._forcedWarp < 0) this._forcedWarp = null;
    this._single = urlParam('vertsingle') === '1';
    // COULOIR TENU (options.steady). Rien de nouveau : les mêmes déformations,
    // bornées. Sur une vitrine, le visuel n'est vu que dix secondes et sans
    // son - il n'a pas le temps de RACONTER une bascule, il est juste vu
    // penché, et le mur proche mange un côté du cadre. Deux bornes, parce que
    // mesuré, annuler le seul point de fuite ne suffit pas : à uVP = 0 le
    // couloir reste tordu par CONVEX. Le mode par défaut ne bouge pas d'un
    // iota, l'app et la télé gardent exactement ce qu'elles avaient.
    const steady = !!(options && options.steady);
    this._vpMax = steady ? VP_MAX * 0.28 : VP_MAX;
    this._vpJump = steady ? VP_JUMP * 0.28 : VP_JUMP;
    this._vpDriftRate = steady ? VP_DRIFT * 0.5 : VP_DRIFT;
    this._steadyAmp = steady ? 0.42 : 1;
    this._forceFlip = urlParam('vertflip') === '1';
    const g = parseInt(urlParam('vertgrid'), 10);
    this._forcedGrid = Number.isFinite(g) && g >= 2 && g <= 40 ? g : null;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const layout = TIER[this._tier];

    // The camera sits on the corridor's axis. Nothing moves it: advancing is
    // the chequer scrolling (see the header).
    camera.position.set(0, 0, 0);
    camera.lookAt(0, 0, -1);

    const seg = layout.seg;
    const geo = new THREE.PlaneGeometry(1, 1, seg, seg);
    const faces = new Float32Array(layout.faces);
    for (let i = 0; i < layout.faces; i++) faces[i] = i;

    // safeZoneUniforms is still spread in, and NOT because of the safe zone -
    // that is gone from the shader. It is where uAspect lives, which scene-
    // manager writes on every resize; the zone's own holders come with it and
    // are ignored, three.js binding only what the program declares.
    this._uniforms = Object.assign({}, safeZoneUniforms, {
      uInvNear: { value: 1 / 0.8 },
      uInvFar: { value: 1 / FAR },
      uTile: { value: 2 * R / TILES_MIN },
      uTravel: { value: 0 },
      uVP: { value: new THREE.Vector2(0, 0) },
      uWarpA: { value: W_STRAIGHT },
      uWarpB: { value: W_STRAIGHT },
      uAmpA: { value: 0 },
      uAmpB: { value: 0 },
      uInvert: { value: 0 },
      uGuard: { value: 0.30 },
      uHalfRes: { value: new THREE.Vector2(640, 360) },
      uStep: { value: new THREE.Vector2(1 / seg, 1 / seg) },
      uColA: { value: new THREE.Color(0x2fd8ff) },
      uColB: { value: new THREE.Color(0x1a0a2e) },
      uColC: { value: new THREE.Color(0xff2fb0) },
      uTri: { value: 0 },
      uRingGain: { value: RING_GAIN_LO },
      uRings: { value: layout.rings ? 1 : 0 },
      uRingCol: { value: new THREE.Color(0xffffff) }
    });

    this._material = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      side: THREE.DoubleSide,
      depthTest: true,
      depthWrite: true
    });

    const igeo = new THREE.InstancedBufferGeometry();
    igeo.index = geo.index;
    igeo.attributes.position = geo.attributes.position;
    igeo.attributes.uv = geo.attributes.uv;
    igeo.setAttribute('aFace', new THREE.InstancedBufferAttribute(faces, 1));
    igeo.instanceCount = layout.faces;
    // The vertex shader ignores `position` entirely - every coordinate comes
    // from uv and aFace - so the bounding sphere three would compute from it
    // is meaningless. Culling is off instead of wrong.
    igeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
    this._geo = igeo;
    this._plane = geo;

    this._mesh = new THREE.Mesh(igeo, this._material);
    this._mesh.frustumCulled = false;
    // The footprint is in PIXELS, so the vertex shader needs the real drawing
    // buffer - which only the renderer knows, and onBeforeRender on the object
    // is the only place a visualizer is handed one.
    const self = this;
    this._mesh.onBeforeRender = function (renderer) {
      renderer.getDrawingBufferSize(_size);
      self._uniforms.uHalfRes.value.set(_size.x * 0.5, _size.y * 0.5);
    };
    scene.add(this._mesh);

    this._bgMat = new THREE.ShaderMaterial({
      uniforms: {}, vertexShader: bgVertexShader, fragmentShader: bgFragmentShader,
      depthTest: false, depthWrite: false
    });
    this._bg = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._bgMat);
    this._bg.frustumCulled = false;
    this._bg.renderOrder = -1000;
    scene.add(this._bg);

    // --- state ---
    this._time = 0;
    this._beats = 0;
    this._lastBeat = -1;
    this._lastBar = -1;
    this._lastPhrase = -1;
    this._lastRealBeats = null;
    this._paused = false;
    this._lowSmooth = 0.5;
    this._highSmooth = 0.5;
    this._vp = new THREE.Vector2(0, 0);
    this._vpDrift = Math.random() * Math.PI * 2;
    this._swapped = false;
    this._lastInvertAt = -1e9;
    this._triUntilBar = -1;
    this._pool = [W_CONVEX, W_TWIST, W_FLIP];
    this._ampScale = 1;
    this._invertUntil = -1;
    this._pendingDropAt = null;
    this._dropRampFrom = -1;
    this._lastPaletteRef = undefined;
    this._palette = [new THREE.Color(0x2fd8ff), new THREE.Color(0xff2fb0), new THREE.Color(0xffd166)];
    this._colHi = new THREE.Color();
    this._colLo = new THREE.Color();
    this._colTri = new THREE.Color();
    this._hsl = { h: 0, s: 0, l: 0 };
    this._lastAspect = -1;
    this._tiles = TILES_MIN;
    this._beatOffset = 0;

    this._reframe(camera.aspect || 1);
    this._applyPalette();
    this._pickWarps();
  }

  // ------------------------------------------------------------- framing ---
  // The near end of the geometry, from the aspect. The first thing to enter
  // frame is the CORNER of the square section aligned with the frame's own
  // corner, so that is what sets it; a fixed value leaves a wedge of
  // background at the edges on a wide screen.
  _reframe(aspect) {
    const tanY = Math.tan(this._camera.fov * Math.PI / 360);
    const tanCorner = tanY * Math.sqrt(1 + aspect * aspect);
    const enter = (R * Math.SQRT2) / Math.max(1e-4, tanCorner);
    // Near enough that the frustum still fits inside the section with the
    // camera pushed as far off-axis as the drift can take it.
    const nearByDrift = (R - CAMERA_DRIFT)
                      / Math.max(1e-4, tanY * Math.max(aspect, 1));
    const near = Math.max(0.15, Math.min(enter * NEAR_MARGIN, nearByDrift));
    const invNear = 1 / near;
    this._uniforms.uInvNear.value = invNear;

    // Where the undeformed tube first touches the frame corner. Everything
    // nearer than this is behind the frame, and is where the deformations are
    // allowed to ramp in - see the guard in place().
    const sEnter = (invNear - 1 / enter) / (invNear - 1 / FAR);
    this._uniforms.uGuard.value = Math.max(0.05, Math.min(0.55, sEnter));
    this._lastAspect = aspect;
  }

  // ------------------------------------------------------------- colours ---
  _applyPalette() {
    const hsl = this._hsl;
    // The lit square: the palette's own hue, forced to a saturation that
    // survives flat fill, and to a luminance that survives full-frame bloom.
    this._palette[0].getHSL(hsl);
    this._colHi.setHSL(hsl.h, Math.max(SAT_MIN, hsl.s), BASE_HI * 0.62);
    // The dark square is the SECOND palette colour, not a darkened first: two
    // hues read as a chequer, one hue at two brightnesses reads as a shadow.
    this._palette[1].getHSL(hsl);
    this._colLo.setHSL(hsl.h, Math.max(SAT_MIN, hsl.s), BASE_LO * 0.85);
    this._palette[2].getHSL(hsl);
    this._colTri.setHSL(hsl.h, Math.max(SAT_MIN, hsl.s), BASE_HI * 0.55);
    this._uniforms.uRingCol.value.setHSL(hsl.h, 0.22, 0.70);
    this._writeChecker();
  }

  _writeChecker() {
    const a = this._uniforms.uColA.value, b = this._uniforms.uColB.value;
    if (this._swapped) { a.copy(this._colLo); b.copy(this._colHi); }
    else { a.copy(this._colHi); b.copy(this._colLo); }
    this._uniforms.uColC.value.copy(this._colTri);
  }

  // ----------------------------------------------------------- the warps ---
  _forbidden(a, b) {
    for (let i = 0; i < FORBIDDEN.length; i++) {
      const f = FORBIDDEN[i];
      if ((a === f[0] && b === f[1]) || (a === f[1] && b === f[0])) return true;
    }
    return false;
  }

  _pickWarps(breath) {
    const u = this._uniforms;
    // THE BREATH IS A WHOLE BEAT, not a half-empty pair. Drawing the second
    // warp from a pool that contains STRAIGHT looks like it honours both "one
    // of the six" and "always two at once", and measured it honours neither:
    // 37% of beats came out with a single deformation, which is exactly the
    // reading ?vertsingle=1 exists to show is wrong. So STRAIGHT is never a
    // partner - it is its own state, once per phrase, on the beat before the
    // phrase turns over, where the ear is already expecting a lift.
    if (breath) {
      u.uWarpA.value = W_STRAIGHT;
      u.uWarpB.value = W_STRAIGHT;
      u.uAmpA.value = 0;
      u.uAmpB.value = 0;
      return;
    }
    if (this._forcedWarp != null) {
      u.uWarpA.value = this._forcedWarp;
      u.uWarpB.value = W_STRAIGHT;
      u.uAmpA.value = 0.9;
      u.uAmpB.value = 0;
      return;
    }
    const pool = this._pool;
    let a = pool[(Math.random() * pool.length) | 0];
    let b = pool[(Math.random() * pool.length) | 0];
    // A handful of tries rather than a loop with no exit: with six warps and
    // one forbidden pair a clash is rare, and a fallback of STRAIGHT is a
    // legal second warp anyway.
    for (let i = 0; i < 8 && (b === a || this._forbidden(a, b)); i++) {
      b = pool[(Math.random() * pool.length) | 0];
    }
    if (b === a || this._forbidden(a, b)) b = (a === W_TWIST) ? W_FLIP : W_TWIST;
    u.uWarpA.value = a;
    u.uWarpB.value = b;
    // DIFFERENT AMPLITUDES, always. Two warps at the same strength average
    // into a single ambiguous shape; one dominant and one secondary reads as
    // a space with something wrong with it, which is the whole point.
    const low = lerp(AMP_LOW_LO, AMP_LOW_HI, this._lowSmooth) * this._ampScale * this._steadyAmp;
    u.uAmpA.value = Math.min(AMP_CEIL, lerp(AMP_A[0], AMP_A[1], Math.random()) * low);
    u.uAmpB.value = this._single ? 0
      : Math.min(AMP_CEIL, lerp(AMP_B[0], AMP_B[1], Math.random()) * low);
  }

  // The available set changes per phrase, so a phrase has a character rather
  // than being a uniform shuffle of all five. Three of the five, which is
  // enough for variety inside a phrase and few enough that the phrase has a
  // recognisable one.
  _rebuildPool() {
    const all = [W_CONVEX, W_CONCAVE, W_TWIST, W_FLIP, W_FOLD];
    for (let i = all.length - 1; i > 0; i--) {
      const j = (Math.random() * (i + 1)) | 0;
      const t = all[i]; all[i] = all[j]; all[j] = t;
    }
    this._pool.length = 0;
    for (let i = 0; i < 3; i++) this._pool.push(all[i]);
  }

  // -------------------------------------------------------------- events ---
  _onBeat(beat) {
    const inPhrase = ((beat % BEATS_PER_PHRASE) + BEATS_PER_PHRASE) % BEATS_PER_PHRASE;
    this._pickWarps(inPhrase === BEATS_PER_PHRASE - 1);
  }

  _onBar(bar) {
    // The chequer inverts. Instant, and the single most effective optical
    // gesture available - but rate-limited, because a drop can land its own
    // inversion on the same frame.
    if (this._time - this._lastInvertAt >= INVERT_MIN_S) {
      this._swapped = !this._swapped;
      this._writeChecker();
      this._lastInvertAt = this._time;
    }
    // And the vanishing point jumps. The corridor appears to tip over at once.
    const a = Math.random() * Math.PI * 2;
    this._vp.x += Math.cos(a) * this._vpJump;
    this._vp.y += Math.sin(a) * this._vpJump;
    const l = this._vp.length();
    if (l > this._vpMax) this._vp.multiplyScalar(this._vpMax / l);
    // The third colour lasts exactly one bar.
    this._uniforms.uTri.value = (bar === this._triUntilBar) ? 1 : 0;
  }

  _onPhrase(bar) {
    this._rebuildPool();
    this._triUntilBar = bar;
    this._uniforms.uTri.value = 1;
  }

  // -------------------------------------------------------------- update ---
  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;          // freezes everything, as asked
    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;

    const director = profile.director || {};
    const bands = director.bands || { low: 0, mid: 0, high: 0 };
    const k = 1 - Math.exp(-dt / 0.16);
    this._lowSmooth += (clamp01(bands.low) - this._lowSmooth) * k;
    this._highSmooth += (clamp01(bands.high) - this._highSmooth) * k;

    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;

    // ONE BEAT COUNTER, real or free-running. Everything downstream - the
    // warp changes, the colour inversion, the advance, the rings - reads this
    // and nothing else, so the piece behaves identically whether or not the
    // track has been analysed. When a real grid appears mid-track the counter
    // is re-anchored to it rather than reset, so nothing jumps.
    const realBeats = (profile.beatIndex != null && profile.beatPhase != null)
      ? profile.beatIndex + profile.beatPhase : null;
    if (realBeats != null) {
      if (this._lastRealBeats == null) {
        // AN INTEGER OFFSET, and it has to be. A fractional one makes
        // floor(realBeats + offset) a subtraction of two nearly equal floats,
        // and on a beat that lands exactly on an integer - which is every beat
        // when the tempo divides the frame rate - the result falls 1e-15 short
        // and floors to the beat BEFORE. Measured: two of twenty-three warp
        // changes arrived one frame late. An integer offset cancels exactly
        // and keeps the tile phase, at the cost of at most half a beat of
        // one-off shift on the frame the analysis first arrives.
        this._beatOffset = Math.round(this._beats - realBeats);
      }
      this._beats = realBeats + this._beatOffset;
      this._lastRealBeats = realBeats;
    } else {
      this._lastRealBeats = null;
      this._beats += dt * bpm / 60;
    }

    const beat = Math.floor(this._beats);
    const bar = Math.floor(this._beats / BEATS_PER_BAR);
    const phrase = Math.floor(this._beats / BEATS_PER_PHRASE);
    if (beat !== this._lastBeat) {
      if (this._lastBeat >= 0) this._onBeat(beat);
      this._lastBeat = beat;
    }
    if (phrase !== this._lastPhrase) {
      if (this._lastPhrase >= 0) this._onPhrase(bar);
      this._lastPhrase = phrase;
    }
    if (bar !== this._lastBar) {
      if (this._lastBar >= 0) this._onBar(bar);
      this._lastBar = bar;
    }

    // ONE TILE PER BEAT, exactly - the counter IS the tile count. Positive:
    // a feature sits at depth (along - uTravel) * uTile, so uTravel rising is
    // that feature coming toward the lens.
    this._uniforms.uTravel.value = this._beats;

    // --- palette
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const vivid = profile.artworkPalette && profile.artworkPalette.vividColors;
      if (vivid && vivid.length) {
        for (let i = 0; i < 3; i++) this._palette[i].set(vivid[i % vivid.length]);
        this._applyPalette();
      }
    }

    // --- density from progress, quantised: a fractional tile count would
    // slide the whole pattern continuously, which is a different effect.
    const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    const want = this._forcedGrid != null ? this._forcedGrid
      : Math.round(lerp(TILES_MIN, TILES_MAX, Math.sin(Math.PI * progress)));
    if (want !== this._tiles) {
      this._tiles = want;
      this._uniforms.uTile.value = 2 * R / want;
    }

    // --- drops
    const ev = director.nextEvent;
    if (ev && (ev.type === 'drop' || ev.type === 'rise')) {
      const until = ev.msUntil / 1000;
      const ramp = DROP_RAMP_BEATS * 60 / bpm;
      if (until > 0 && until <= ramp) {
        // The START of the ramp is latched once; the ARRIVAL keeps being
        // refined as the countdown sharpens. Re-latching both every frame -
        // which is the obvious way to write this - moves the start and the
        // end together, so the ramp's progress is permanently zero and the
        // four beats before a drop do nothing at all.
        if (this._pendingDropAt == null) this._dropRampFrom = this._time;
        this._pendingDropAt = this._time + until;
      }
    }
    if (this._forceFlip) { this._forceFlip = false; this._pendingDropAt = this._time; }
    if (this._pendingDropAt != null && this._time >= this._pendingDropAt) {
      this._pendingDropAt = null;
      this._invertUntil = this._time + INVERT_BEATS * 60 / bpm;
      this._dropRampFrom = -1;
      if (this._time - this._lastInvertAt >= INVERT_MIN_S) {
        this._swapped = !this._swapped;
        this._writeChecker();
        this._lastInvertAt = this._time;
      }
    }
    // The four beats before a drop push the deformations harder.
    if (this._dropRampFrom > 0 && this._pendingDropAt != null) {
      const span = Math.max(1e-3, this._pendingDropAt - this._dropRampFrom);
      this._ampScale = lerp(1, DROP_RAMP_AMP, clamp01((this._time - this._dropRampFrom) / span));
    } else {
      this._ampScale += (1 - this._ampScale) * Math.min(1, dt * 3);
    }
    const inverting = this._time < this._invertUntil;
    const inv = this._uniforms.uInvert;
    inv.value += ((inverting ? 1 : 0) - inv.value) * Math.min(1, dt * (inverting ? 24 : 6));
    if (!inverting && this._invertUntil > 0 && this._time < this._invertUntil + 0.2) {
      if (profile.director) profile.director.flash = 1;
      this._invertUntil = -1;
    }

    // --- the vanishing point drifts between its jumps. Slow, and capped: the
    // comfort limit is on ROTATION rate, and a drift this size turns the far
    // end by well under the 90 deg/s ceiling.
    this._vpDrift += dt * 0.37;
    this._vp.x += Math.cos(this._vpDrift) * this._vpDriftRate * dt;
    this._vp.y += Math.sin(this._vpDrift * 0.73) * this._vpDriftRate * dt;
    const l = this._vp.length();
    if (l > this._vpMax) this._vp.multiplyScalar(this._vpMax / l);
    this._uniforms.uVP.value.copy(this._vp);

    // --- rings follow the highs
    this._uniforms.uRingGain.value = lerp(RING_GAIN_LO, RING_GAIN_HI, this._highSmooth);

    // --- framing, checked every frame because a resize has no callback here
    const aspect = this._camera.aspect || 1;
    if (Math.abs(aspect - this._lastAspect) > 1e-3) this._reframe(aspect);
  }

  dispose() {
    this._mesh.onBeforeRender = null;
    this._scene.remove(this._mesh);
    this._scene.remove(this._bg);
    this._geo.dispose();
    this._plane.dispose();
    this._material.dispose();
    this._bg.geometry.dispose();
    this._bgMat.dispose();
  }
}

const _size = new THREE.Vector2();
